import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { BUILDER_GUIDE } from "@/config";
import { Breadcrumbs, Heading, type BreadcrumbItem } from "@/ui";

import styles from "./Breadcrumb.module.scss";

export type SubHeaderRoute = {
    label?: string;
    route?: string;
    root?: boolean;
    guide?: boolean;
    breadcrumb?: Array<{ label: string; route: string }>;
};

export type SubHeaderBreadcrumbProps = {
    route?: SubHeaderRoute;
};

/**
 * Shell breadcrumbs built from `routes` metadata — renders `@/ui` `Breadcrumbs`
 * with React Router `Link` for `to` segments.
 */
const SubHeaderBreadcrumb = ({ route }: SubHeaderBreadcrumbProps) => {
    const items = useMemo((): BreadcrumbItem[] => {
        if (route?.guide) {
            return [];
        }

        const out: BreadcrumbItem[] = [];

        if (route && !route.root && !route.guide) {
            out.push({
                id: "settings-root",
                label: "Settings",
                to: "/",
            });
        }

        if (route?.breadcrumb?.length) {
            route.breadcrumb.forEach((b, idx) => {
                out.push({
                    id: `${b.route}-${idx}`,
                    label: b.label,
                    to: b.route,
                });
            });
        }

        out.push({
            id: "current",
            label: route?.label ?? "Settings",
        });

        return out;
    }, [route]);

    if (route?.guide) {
        return (
            <Heading className={styles.guideTitle} level={4}>
                {BUILDER_GUIDE?.title}
            </Heading>
        );
    }

    /* Legacy: Settings home used a 24px page title; nested trails stayed ~14px. */
    const isSettingsHome = Boolean(route?.root && !route?.guide);

    return (
        <Breadcrumbs
            aria-label="Breadcrumb"
            className={styles.breadcrumbs}
            data-ui="subheader-breadcrumb"
            items={items}
            routerLinkAs={RouterLink}
            size={isSettingsHome ? "lg" : "md"}
        />
    );
};

export default SubHeaderBreadcrumb;
